import { useState, type FormEvent } from "react";
import { Aviso, Botao, Campo, CampoLongo, Cartao, Chip } from "../componentes/ui";
import { mensagemDoConsole } from "../nucleo/console";
import { nomeDaOrganizacao, useOrganizacoesDoCadastro } from "../nucleo/cadastro";
import {
  AVISO_EM_BRANCO,
  ROTULO_DO_ESTADO_DO_AVISO,
  corpoDoAviso,
  useArquivarAviso,
  useAvisosDaGestao,
  usePublicarAviso,
  validarAviso,
  type AvisoDaGestao,
  type EstadoDoAviso,
  type FormularioDeAviso,
} from "../nucleo/avisos";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";
import { useVoltarParaHome } from "../nucleo/voltarParaHome";

const CHIP_DO_ESTADO: Record<EstadoDoAviso, "ok" | "aguardando" | "neutro" | "erro"> = {
  no_ar: "ok",
  agendado: "aguardando",
  expirado: "neutro",
  arquivado: "neutro",
};

function dataHora(valor: string | null): string {
  if (!valor) return "sem data";
  const data = new Date(valor);
  return Number.isNaN(data.getTime())
    ? "—"
    : new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(data);
}

/** O mural: a equipe publica o aviso para todas as empresas ou para algumas, com vigência. */
export function MuralDeAvisos() {
  useTitulo("Mural de avisos");
  const sessao = useSessao();
  const podeGerenciar = podeNaSessao(sessao.data, "aviso:gerenciar");
  const avisos = useAvisosDaGestao(podeGerenciar);
  const arquivar = useArquivarAviso();

  if (!sessao.data) {
    return (
      <p className="lead" role="status">
        Verificando o seu acesso…
      </p>
    );
  }
  if (!podeGerenciar) {
    return (
      <Aviso tom="marca" data-testid="mural-sem-permissao">
        O mural de avisos é mantido pela equipe da GEOvendas.
      </Aviso>
    );
  }

  return (
    <div data-testid="mural-de-avisos-da-gestao">
      <h1 className="titulo-pagina">Mural de avisos</h1>
      <p className="lead mb-4">
        Recados para o painel dos clientes: aparecem para as empresas que você escolher até o aviso
        expirar.
      </p>
      <FormularioDeNovoAviso />
      <section aria-labelledby="titulo-dos-avisos" className="mt-6">
        <h2 className="titulo-secao" id="titulo-dos-avisos">
          Avisos publicados
        </h2>
        {avisos.isPending ? <p role="status">Carregando avisos…</p> : null}
        {avisos.isError ? <Aviso tom="erro">{mensagemDoConsole(avisos.error)}</Aviso> : null}
        {avisos.data && avisos.data.length === 0 ? <Aviso tom="marca">Nenhum aviso publicado.</Aviso> : null}
        {arquivar.isError ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(arquivar.error)}</Aviso> : null}
        <div className="lista-de-itens">
          {avisos.data?.map((aviso) => (
            <CartaoDoAviso key={aviso.id} aviso={aviso} aoArquivar={() => arquivar.mutate(aviso.id)} ocupado={arquivar.isPending} />
          ))}
        </div>
      </section>
    </div>
  );
}

function CartaoDoAviso({
  aviso,
  aoArquivar,
  ocupado,
}: {
  aviso: AvisoDaGestao;
  aoArquivar: () => void;
  ocupado: boolean;
}) {
  return (
    <Cartao
      titulo={aviso.titulo}
      data-testid={`aviso-${aviso.titulo}`}
      data-estado={aviso.estado}
      acessorio={<Chip estado={CHIP_DO_ESTADO[aviso.estado]}>{ROTULO_DO_ESTADO_DO_AVISO[aviso.estado]}</Chip>}
    >
      <p className="mb-2">{aviso.corpo}</p>
      <p className="nota-rodape">
        Para{" "}
        {aviso.todas_organizacoes
          ? "todas as organizações"
          : aviso.organizacoes.map((organizacao) => organizacao.nome).join(", ")}
        {" · "}de {dataHora(aviso.inicio_em)} até {dataHora(aviso.expira_em)}
        {aviso.criado_por_nome ? ` · por ${aviso.criado_por_nome}` : ""}
      </p>
      {aviso.estado !== "arquivado" ? (
        <Botao
          tamanho="pequeno"
          variante="fantasma"
          className="mt-2"
          aria-label={`Arquivar o aviso ${aviso.titulo}`}
          disabled={ocupado}
          onClick={aoArquivar}
        >
          Arquivar
        </Botao>
      ) : null}
    </Cartao>
  );
}

function FormularioDeNovoAviso() {
  const organizacoes = useOrganizacoesDoCadastro();
  const publicar = usePublicarAviso();
  const voltarParaHome = useVoltarParaHome();
  const [form, setForm] = useState<FormularioDeAviso>(AVISO_EM_BRANCO);
  const [problema, setProblema] = useState<string | null>(null);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const erro = validarAviso(form);
    setProblema(erro);
    if (erro) return;
    publicar.mutate(corpoDoAviso(form), {
      onSuccess: () => {
        setForm(AVISO_EM_BRANCO);
        voltarParaHome("Aviso publicado.");
      },
    });
  }

  return (
    <Cartao titulo="Novo aviso" data-testid="formulario-de-aviso">
      <form onSubmit={enviar} noValidate>
        <Campo
          rotulo="Título"
          obrigatorio
          value={form.titulo}
          onChange={(e) => setForm({ ...form, titulo: e.target.value })}
        />
        <CampoLongo
          rotulo="Texto"
          obrigatorio
          value={form.corpo}
          onChange={(e) => setForm({ ...form, corpo: e.target.value })}
        />
        <div className="grid gap-3 md:grid-cols-2">
          <Campo
            rotulo="Começa em (vazio = agora)"
            type="datetime-local"
            value={form.inicio}
            onChange={(e) => setForm({ ...form, inicio: e.target.value })}
          />
          <Campo
            rotulo="Expira em (vazio = não expira)"
            type="datetime-local"
            value={form.expira}
            onChange={(e) => setForm({ ...form, expira: e.target.value })}
          />
        </div>
        <fieldset className="mb-3">
          <legend className="campo__rotulo">Para quem</legend>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.todas}
              onChange={(e) => setForm({ ...form, todas: e.target.checked })}
            />
            <span>Todas as organizações</span>
          </label>
          {!form.todas ? (
            <div className="mt-2" data-testid="destinos-do-aviso">
              {(organizacoes.data ?? []).map((organizacao) => (
                <label key={organizacao.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.organizacaoIds.includes(organizacao.id)}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        organizacaoIds: e.target.checked
                          ? [...form.organizacaoIds, organizacao.id]
                          : form.organizacaoIds.filter((id) => id !== organizacao.id),
                      })
                    }
                  />
                  <span>{nomeDaOrganizacao(organizacao)}</span>
                </label>
              ))}
            </div>
          ) : null}
        </fieldset>
        {problema ? <Aviso tom="erro" className="mb-3">{problema}</Aviso> : null}
        {publicar.isError ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(publicar.error)}</Aviso> : null}
        <Botao type="submit" disabled={publicar.isPending} data-testid="publicar-aviso">
          {publicar.isPending ? "Publicando…" : "Publicar aviso"}
        </Botao>
      </form>
    </Cartao>
  );
}
